import TileLayer from "@arcgis/core/layers/TileLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";

import getBasemap from "../../components/ArcGISMap/getBaseMap";
import HomeViewModel from "@arcgis/core/widgets/Home/HomeViewModel";

export type MapState = {
  mapLoaded: boolean;
  mapView: __esri.MapView | null;
  map: __esri.Map | null;
  selectedOids: number[];
  tooltipText: string;
  selectionGraphic: __esri.Graphic | null;
  bufferGraphic: __esri.Graphic | null;
  scaleType: string;
  scale: number;
  zoom: number;
  extentHistory: __esri.Extent[];
  navigationMode: "pan" | "zoomIn" | "zoomOut";
  highlightHandle: __esri.Handle | null;
  isMobile: boolean;
  isTablet: boolean;
  originalExtent: __esri.Extent | null;
  globalSelectedBasemap: any | null;
};

export type MapActions = {
  setZoom: (zoom: number) => void;
  setScale: (scale: number) => void;
  setScaleType: (type: string) => void;
  setIsMobile: (isMobile: boolean) => void;
  setIsTablet: (isTablet: boolean) => void;
  setMapView: (mapView: __esri.MapView) => void;
  setMapLoaded: (loaded: boolean) => void;
  setMap: (map: __esri.Map) => void;
  addHighlights: (oids: number[]) => void;
  setSelectedOids: (oids: number[]) => void;
  setTooltipText: (text: string) => void;
  setSelectionGraphic: (graphic: __esri.Graphic) => void;
  setBufferGraphic: (graphic: __esri.Graphic | null) => void;
  zoomToExtent: (extent: __esri.Extent) => void;
  zoomToGraphic: (graphic: __esri.Graphic) => void;
  zoomToOids: (oids: number[]) => void;
  zoomToResults: () => void;
  clearMap: () => void;
  addExtentToHistory: (extent: __esri.Extent) => void;
  setNavigationMode: (mode: "pan" | "zoomIn" | "zoomOut") => void;
  goToOriginalExtent: () => void;
  setOriginalExtent: (extent: __esri.Extent) => void;
  setGlobalSelectedBasemap: (basemap: any) => void;
};

export const createMapSlice = (set, get): MapState & MapActions => ({
  isMobile: window.innerWidth <= 768,
  setIsMobile: (isMobile) => set({ isMobile }),
  globalSelectedBasemap: null,
  setGlobalSelectedBasemap: (basemap) => {
    set({ globalSelectedBasemap: basemap });
    // setTimeout(() => {
    //   const mapView = get().mapView;
    //   // loop through all layers and refresh
    //   mapView?.map.layers.forEach((layer) => {
    //     layer?.refresh();
    //   });
    // }, 50);
  },
  isTablet: window.innerWidth <= 1024 && window.innerWidth > 768,
  setIsTablet: (isTablet) => set({ isTablet }),
  zoom: 0,
  setZoom: (zoom) => {
    const mapView = get().mapView;
    if (mapView && zoom !== mapView.zoom) {
      mapView.zoom = zoom;
    }
    set({ zoom });
  },
  scale: 0,
  setScale: (scale) => {
    const mapView = get().mapView;
    if (mapView) {
      mapView.scale = scale;
    }
    set({ scale });
  },
  scaleType: "default",
  setScaleType: (type) => {
    const mapView = get().mapView;
    if (mapView) {
      mapView.constraints.snapToZoom = type === "default";
      mapView.map.basemap = getBasemap(get().config.streetsBasemap, type);
    }

    //Reverse for loop
    for (let i = mapView.map.layers.length - 1; i >= 0; i--) {
      const layer = mapView.map.layers.getItemAt(i);
      const secondaryLayers = get().config.secondaryLayers;
      const configLayer = secondaryLayers.find((_configLayer) => {
        const _configId = _configLayer.id?.toLowerCase() || "";
        return _configId === layer.id?.toLowerCase();
      });
      if (
        !configLayer ||
        !configLayer.scaleUrl ||
        configLayer.scaleUrl === ""
      ) {
        continue;
      }

      const configCopy = {
        ...configLayer,
      };
      delete configCopy.type;
      delete configCopy.visible;
      delete configCopy.opacity;
      const mainUrl = configCopy.url;
      delete configCopy.url;
      configCopy.opacity = layer.opacity;
      configCopy.visible = layer.visible;

      if (type === "scale") {
        const newLayer = new MapImageLayer({
          url: layer.scaleUrl,
          ...configCopy,
        });
        mapView.map.layers.removeAt(i);
        mapView.map.layers.add(newLayer, i);
      } else if (type === "default") {
        delete configCopy.sublayers;
        const newLayer = new TileLayer({
          url: mainUrl,
          ...configCopy,
        });

        mapView.map.layers.removeAt(i);
        mapView.map.layers.add(newLayer, i);
      }
      // }
    }

    set({ scaleType: type });
  },
  tooltipText: "",
  setTooltipText: (text) => set({ tooltipText: text }),
  mapLoaded: false,
  setMapLoaded: (loaded) => set({ mapLoaded: loaded }),
  mapView: null,
  setMapView: (mapView) => {
    set({ mapView, originalExtent: mapView?.extent });
  },
  map: null,
  setMap: (map) => set({ map }),
  zoomToOids: async (oids: number[]) => {
    const primaryQueryLayer = get().primaryQueryLayer;
    if (!primaryQueryLayer) {
      return;
    }
    const res = await primaryQueryLayer.queryFeatures({
      objectIds: oids,
      returnGeometry: true,
    });
    const extent = res.features[0].geometry.extent;
    get().zoomToExtent(extent);
  },
  zoomToResults: () => {
    const mapView = get().mapView;
    if (!mapView) {
      return;
    }
    const results = get().results;

    if (results && results.length > 0) {
      mapView.goTo(results);
    }
  },
  zoomToExtent: (extent: __esri.Extent) => {
    const mapView = get().mapView;
    if (!mapView) {
      return;
    }
    mapView.goTo(extent);
  },
  zoomToGraphic: (graphic: __esri.Graphic) => {
    const mapView = get().mapView;
    if (!mapView) {
      return;
    }
    const extent = graphic.geometry.extent;
    mapView.goTo(extent.expand(1.5));
  },
  highlightHandle: null,
  addHighlights: async (oids: number[]) => {
    const primaryLayerView = get().primaryLayerView;
    if (!primaryLayerView) {
      return;
    }
    const highlightHandle = get().highlightHandle;
    if (highlightHandle) {
      highlightHandle.remove();
    }
    if (oids.length === 0) {
      return;
    }

    const _newHandle = primaryLayerView.highlight(oids);
    set({ highlightHandle: _newHandle });
  },
  selectedOids: [],
  setSelectedOids: async (objectIds) => {
    get().addHighlights(objectIds);
    set({ selectedOids: objectIds, selectionGraphic: null });
  },
  selectionGraphic: null,
  setSelectionGraphic: async (graphic) => {
    const primaryQueryLayer = get().primaryQueryLayer;
    const bufferLayer = get().bufferLayer;
    if (!primaryQueryLayer) {
      return;
    }
    const res = await primaryQueryLayer.queryFeatures({
      geometry: graphic.geometry,
      outFields: ["*"],
      returnGeometry: true,
    });
    get().addResults(res.features.map((f) => f));

    set({ selectionGraphic: graphic, activeResultsType: "parcel" });
    bufferLayer?.graphics.removeAll();
  },
  clearMap: () => {
    const highlightLayer = get().highlightLayer;
    const bufferLayer = get().bufferLayer;
    const markupLayer = get().markupLayer;
    const hoverHighlightLayer = get().hoverHighlightLayer;
    highlightLayer?.graphics.removeAll();
    bufferLayer?.graphics.removeAll();
    markupLayer?.graphics.removeAll();

    set({ results: [], selectionGraphic: null, bufferGraphic: null });
  },
  bufferGraphic: null,
  setBufferGraphic: (graphic) => set({ bufferGraphic: graphic }),
  extentHistory: [],
  addExtentToHistory: (extent) => {
    if (!extent) {
      return;
    }
    const _extentHistory = get().extentHistory;
    const anyEqual = _extentHistory.some((e) => {
      return (
        e.xmin === extent.xmin &&
        e.xmax === extent.xmax &&
        e.ymin === extent.ymin &&
        e.ymax === extent.ymax
      );
    });
    if (!anyEqual) {
      set({ extentHistory: [...get().extentHistory, extent] });
    }
  },
  originalExtent: null,
  setOriginalExtent: (extent) => {
    set({ originalExtent: extent });
  },
  navigationMode: "pan",
  setNavigationMode: (mode) => {
    set({ navigationMode: mode });
  },
  goToOriginalExtent: () => {
    const mapView = get().mapView;
    const originalExtent = get().originalExtent;
    if (!mapView || !originalExtent) {
      return;
    }
    mapView.goTo(originalExtent);
  },
});
